import { useMemo } from 'react';
import { useGetOrganizationBySlug } from '@wbk/api/hooks';
import { useRouter } from 'next/router';
import { Exception } from '@wbk/logger';

const useSso = () => {
  const { locale, query, asPath, push } = useRouter();
  const orgSlug = process.env.NEXT_PUBLIC_ORGANIZATION_SLUG!;
  const { data, isLoading } = useGetOrganizationBySlug(
    { slug: orgSlug, lang: locale },
    { enabled: !!orgSlug }
  );

  const sso = data?.sso_enabled ? data.sso_configuration : undefined;
  // Next hands back an array when a query param repeats
  const redirectParam = Array.isArray(query.redirect) ? query.redirect[0] : query.redirect;
  const returnTo = redirectParam || asPath;
  const loginPath = returnTo.startsWith('/login')
    ? '/login'
    : `/login?redirect=${encodeURIComponent(returnTo)}`;

  const authorizationUrl = useMemo(() => {
    if (typeof window === 'undefined' || !sso?.client_id || !sso.authorization_endpoint) {
      return null;
    }

    try {
      const url = new URL(sso.authorization_endpoint);
      url.searchParams.set('client_id', sso.client_id);
      url.searchParams.set('redirect_uri', `${window.location.origin}/auth/${sso.type}/callback`);
      url.searchParams.set('response_type', 'code');
      url.searchParams.set('scope', (sso.scopes || ['openid']).join(' '));
      url.searchParams.set('state', returnTo);

      return url.href;
    } catch {
      return null;
    }
  }, [returnTo, sso]);

  // Null only while the org config resolves; `window.location` needs the locale prefix that `push` adds itself
  const callbackUri = isLoading
    ? null
    : authorizationUrl || `${locale ? `/${locale}` : ''}${loginPath}`;

  const reportMisconfiguration = (name: string, message: string) => {
    if (data?.sso_enabled) {
      new Exception({
        name,
        error: new Error(message),
        context: { extra: { sso: data.sso_configuration } },
      });
    }
  };

  const redirectToSso = () => {
    if (isLoading) {
      return;
    }

    if (!authorizationUrl) {
      reportMisconfiguration('SsoLoginWithoutCallbackUrl', 'sso configuration is unusable');
      push(loginPath);
      return;
    }

    window.location.assign(authorizationUrl);
  };

  const redirectToProfile = () => {
    if (isLoading) {
      return;
    }

    if (!sso?.profile_url) {
      reportMisconfiguration('SsoProfileWithoutCallbackUrl', 'sso.profile_url is undefined');
      push('/profile');
      return;
    }

    window.location.assign(sso.profile_url);
  };

  return {
    sso,
    loading: isLoading,
    callbackUri,
    redirectToSso,
    redirectToProfile,
  };
};

export default useSso;
